import { useState, useCallback, useMemo } from "react";

/**
 * Custom hook to manage term type and length state with better performance
 * @param {number} defaultMonthTerm - Default months value
 * @param {number} defaultWeekTerm - Default weeks value
 * @returns {object} State and setters for term type and length
 */
const useTermLength = (defaultMonthTerm, defaultWeekTerm) => {
  // Validate defaults with fallbacks
  const validMonthDefault = useMemo(
    () =>
      typeof defaultMonthTerm === "number" && !isNaN(defaultMonthTerm)
        ? defaultMonthTerm
        : 12,
    [defaultMonthTerm]
  );

  const validWeekDefault = useMemo(
    () =>
      typeof defaultWeekTerm === "number" && !isNaN(defaultWeekTerm)
        ? defaultWeekTerm
        : 52,
    [defaultWeekTerm]
  );

  // State
  const [termType, setTermType] = useState("months");
  const [monthTerm, setMonthTerm] = useState(validMonthDefault);
  const [weekTerm, setWeekTerm] = useState(validWeekDefault);

  // Memoize the current term length
  const termLength = useMemo(
    () => (termType === "weeks" ? weekTerm : monthTerm),
    [termType, weekTerm, monthTerm]
  );

  // Optimize setTermLength with useCallback
  const setTermLength = useCallback(
    (value) => {
      if (typeof value !== "number" || isNaN(value)) {
        console.error("Invalid term length value:", value);
        return;
      }

      if (termType === "weeks") {
        setWeekTerm(value);
      } else {
        setMonthTerm(value);
      }
    },
    [termType]
  );

  return { termType, setTermType, termLength, setTermLength };
};

export { useTermLength };
